/**
 * ApplicationFacts — что мы знаем о заявке, списком строк.
 *
 * Строки неодинаковые для двух происхождений заявки: у лида с
 * лендинга есть имя и нет клиники, у заявки из приложения наоборот.
 * Поэтому «Имя», «Клиника» и «Источник» рисуются только когда
 * заполнены — пустая строка в карточке читается как «не загрузилось»,
 * а не как «не спрашивали».
 *
 * «Город» и «Размер клиники» остаются всегда с прочерком: это поля,
 * которые менеджер ищет глазами на одном и том же месте, и их
 * исчезновение путает сильнее прочерка.
 */

import type {ReactNode} from 'react';

import type {ApplicationItem} from '@services/applications';

import {doctorsLabel, fmtAppDate, fmtAppPhone, orDash, STATUS_LABEL} from '../Applications.utils';

type Props = {
  application: ApplicationItem;
};

export const ApplicationFacts = ({application: a}: Props) => (
  <div className="rounded-[13px] border border-skr-line bg-card">
    {a.fullName !== null && <Row label="Имя">{a.fullName}</Row>}
    {a.companyName !== null && <Row label="Клиника">{a.companyName}</Row>}
    <Row label="Город">{orDash(a.cityName)}</Row>
    <Row label="Размер клиники">{doctorsLabel(a.doctorsCount)}</Row>
    <Row label="Телефон">
      <span className="font-mono">+996 {fmtAppPhone(a.phone)}</span>
      {!a.phoneVerified && (
        <span className="ml-2 rounded-full bg-skr-warn/14 px-[7px] py-px text-[11px] font-semibold text-skr-warn">
          не подтверждён
        </span>
      )}
    </Row>
    {a.source !== null && <Row label="Источник">{a.source}</Row>}
    <Row label="Статус">{STATUS_LABEL[a.status]}</Row>
    {a.reviewedByName !== null && (
      <Row label="Рассмотрел">
        {a.reviewedByName}
        {a.reviewedAt !== null && (
          <span className="text-muted-foreground"> · {fmtAppDate(a.reviewedAt)}</span>
        )}
      </Row>
    )}
  </div>
);

const Row = ({label, children}: {label: string; children: ReactNode}) => (
  <div className="flex items-center justify-between border-b border-skr-hairline px-4 py-[11px] text-[13px] last:border-b-0">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-right font-medium text-foreground">{children}</span>
  </div>
);
